import React, { useState, useMemo } from "react";

const isProbablyExternalDomain = (value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  if (/^https?:\/\//i.test(v)) return true;
  if (/^www\./i.test(v)) return true;
  if (/^[^\s/]+\.[^\s/]{2,}([/?#].*)?$/i.test(v)) return true;
  return false;
};

const normalizeHref = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  const lower = v.toLowerCase();

  // keep mail / tel
  if (lower.startsWith("mailto:")) return v;
  if (lower.startsWith("tel:")) return v;

  // phone number -> WhatsApp
  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // wa:972... or whatsapp:972...
  if (lower.startsWith("wa:") || lower.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  // malformed protocol fixes
  if (/^https\/\/:/i.test(v)) {
    return v.replace(/^https\/\/:/i, "https://");
  }

  if (/^http\/\/:/i.test(v)) {
    return v.replace(/^http\/\/:/i, "http://");
  }

  if (/^https:\/(?!\/)/i.test(v)) {
    return v.replace(/^https:\/(?!\/)/i, "https://");
  }

  if (/^http:\/(?!\/)/i.test(v)) {
    return v.replace(/^http:\/(?!\/)/i, "http://");
  }

  // already valid absolute URL
  if (/^https?:\/\//i.test(v)) return v;

  // wa.me direct domain
  if (/^wa\.me\//i.test(v)) return `https://${v}`;

  // www.domain.com
  if (/^www\./i.test(v)) return `https://${v}`;

  // plain domain like example.com or sub.example.com/path
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?$/i.test(v)) {
    return `https://${v}`;
  }

  // internal path
  if (v.startsWith("/")) return v;

  // fallback internal slug/path
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal =
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href);

  if (isExternal) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const pickAlign = (style, config, fallback = "center") => {
  const v = String(style?.textAlign || style?.alignment || config?.alignment || "")
    .toLowerCase()
    .trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const renderActionIcon = (service) => {
  if (service?.buttonIcon) {
    return <img src={service.buttonIcon} alt="" className="w-5 h-5 object-contain" />;
  }

  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0-7L10 14m-4 0H3v7h7" />
    </svg>
  );
};

export default function ServiceSection3({ config = {}, style = {} }) {
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const bg = buildBg(style, "bg-gray-50");

  const align = pickAlign(style, config, "center");
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const alignClass = alignMap[align] || "text-center";

  const services = useMemo(() => {
    const src = Array.isArray(config?.services) ? config.services : [];

    const normalized = src.map((s, idx) => {
      const priceObj =
        s?.price && typeof s.price === "object" && !Array.isArray(s.price) ? s.price : {};

      return {
        id: s?.id ?? idx + 1,
        name: s?.name ?? "",
        description: s?.description ?? "",
        price: {
          monthly: priceObj.monthly ?? "",
          yearly: priceObj.yearly ?? "",
          onetime: priceObj.onetime ?? "",
        },
        period: s?.period ?? "/month",
        features: Array.isArray(s?.features) ? s.features : [],
        limitations: Array.isArray(s?.limitations) ? s.limitations : [],
        buttonLink: s?.buttonLink ?? "",
        buttonIcon: s?.buttonIcon ?? "",
        highlighted: Boolean(s?.highlighted),
        popular: Boolean(s?.popular),
      };
    });

    if (normalized.length) return normalized;

    return [
      {
        id: 1,
        name: "Starter",
        description: "Perfect for small businesses just getting started",
        price: { monthly: "$29", yearly: "$290", onetime: "$499" },
        period: "/month",
        features: ["Up to 5 users", "Basic analytics", "Email support", "1GB storage", "API access"],
        limitations: ["No custom branding", "Limited reports"],
        buttonLink: "/signup/starter",
        buttonIcon: "",
        highlighted: false,
        popular: false,
      },
      {
        id: 2,
        name: "Professional",
        description: "Ideal for growing businesses with advanced needs",
        price: { monthly: "$79", yearly: "$790", onetime: "$1,299" },
        period: "/month",
        features: ["Up to 20 users", "Advanced analytics", "Priority support", "10GB storage", "Custom branding"],
        limitations: [],
        buttonLink: "example.com",
        buttonIcon: "",
        highlighted: true,
        popular: true,
      },
      {
        id: 3,
        name: "Enterprise",
        description: "For large organizations with custom requirements",
        price: { monthly: "$199", yearly: "$1,990", onetime: "Custom" },
        period: "/month",
        features: ["Unlimited users", "Custom analytics", "24/7 phone support", "Dedicated account manager"],
        limitations: [],
        buttonLink: "wa:972501234567",
        buttonIcon: "",
        highlighted: false,
        popular: false,
      },
    ];
  }, [config?.services]);

  return (
    <section dir={config?.direction || "ltr"} style={bg.style} className={`py-14 px-4 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-12`}>
          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            {config?.title || "Simple, Transparent Pricing"}
          </h2>
          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
          >
            {config?.subtitle || "Choose the plan that's right for your business"}
          </p>

          <div
            style={{ backgroundColor: style?.servicePricingToggleBg || undefined }}
            className="inline-flex items-center gap-4 p-1 bg-white rounded-full shadow-md"
          >
            <button
              type="button"
              onClick={() => setBillingPeriod("monthly")}
              style={{
                backgroundColor:
                  billingPeriod === "monthly"
                    ? style?.servicePricingToggleActiveBg || undefined
                    : undefined,
                color:
                  billingPeriod === "monthly"
                    ? style?.servicePricingToggleActiveText || undefined
                    : style?.servicePricingToggleInactiveText || undefined,
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                billingPeriod === "monthly" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>

            <button
              type="button"
              onClick={() => setBillingPeriod("yearly")}
              style={{
                backgroundColor:
                  billingPeriod === "yearly"
                    ? style?.servicePricingToggleActiveBg || undefined
                    : undefined,
                color:
                  billingPeriod === "yearly"
                    ? style?.servicePricingToggleActiveText || undefined
                    : style?.servicePricingToggleInactiveText || undefined,
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                billingPeriod === "yearly" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Yearly <span className="text-xs opacity-75">Save 20%</span>
            </button>

            <button
              type="button"
              onClick={() => setBillingPeriod("onetime")}
              style={{
                backgroundColor:
                  billingPeriod === "onetime"
                    ? style?.servicePricingToggleActiveBg || undefined
                    : undefined,
                color:
                  billingPeriod === "onetime"
                    ? style?.servicePricingToggleActiveText || undefined
                    : style?.servicePricingToggleInactiveText || undefined,
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                billingPeriod === "onetime" ? "bg-blue-600 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              One-time
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {services.map((service) => {
            const priceText = service?.price?.[billingPeriod] ?? "";
            const isCustom = priceText === "Custom";

            return (
              <div
                key={service.id}
                style={{
                  backgroundColor: style?.serviceCardBg || undefined,
                  borderColor:
                    service.highlighted
                      ? style?.serviceHighlightedRing || undefined
                      : style?.serviceCardBorder || undefined,
                }}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
                  service.highlighted ? "ring-2 ring-blue-500 md:scale-105" : ""
                } ${!service.highlighted && style?.serviceCardBorder ? "border" : ""}`}
              >
                {service.popular && (
                  <div
                    style={{
                      backgroundColor: style?.servicePopularBadgeBg || undefined,
                      color: style?.servicePopularBadgeText || undefined,
                    }}
                    className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 text-sm font-semibold"
                  >
                    Most Popular
                  </div>
                )}

                <div className={`p-8 ${service.popular ? "pt-12" : ""}`}>
                  <h3
                    style={{ color: style?.serviceCardTitleColor || undefined }}
                    className="text-2xl font-bold text-gray-900 mb-2"
                  >
                    {service.name}
                  </h3>

                  <p
                    style={{ color: style?.serviceCardDescriptionColor || undefined }}
                    className="text-gray-600 text-sm mb-6"
                  >
                    {service.description}
                  </p>

                  <div className="mb-6">
                    <span
                      style={{ color: style?.servicePriceColor || undefined }}
                      className="text-4xl font-bold text-gray-900"
                    >
                      {priceText}
                    </span>

                    {!isCustom && priceText ? (
                      <span
                        style={{ color: style?.servicePeriodColor || undefined }}
                        className="text-gray-600 ml-2"
                      >
                        {billingPeriod === "onetime" ? "one-time" : service.period}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <svg
                          style={{ color: style?.serviceFeatureIconColor || undefined }}
                          className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span
                          style={{ color: style?.serviceFeatureTextColor || undefined }}
                          className="text-gray-600 text-sm"
                        >
                          {feature}
                        </span>
                      </div>
                    ))}

                    {service.limitations.map((limitation, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <svg
                          style={{ color: style?.serviceXIconColor || undefined }}
                          className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span
                          style={{ color: style?.serviceLimitationsColor || undefined }}
                          className="text-gray-400 text-sm"
                        >
                          {limitation}
                        </span>
                      </div>
                    ))}
                  </div>

                 
                </div>
              </div>
            );
          })}
        </div>

        <div className={`${alignClass} mt-12`}>
          <p
            style={{ color: style?.serviceMutedTextColor || undefined }}
            className="text-gray-600 mb-4"
          >
            {config?.additionalInfo || "All plans include a 14-day free trial. No credit card required."}
          </p>

        </div>
      </div>
    </section>
  );
}